import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { OrderPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Breadcrumbs } from "@/components/breadcrumbs";

function fmtMoney(cents: number, currency: string): string {
  return `${currency === "ZAR" ? "R " : `${currency} `}${(cents / 100).toFixed(2)}`;
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-ZA", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function MyOrderDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const justSubmitted = params.get("submitted") === "1";

  const [order, setOrder] = useState<OrderPublic | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    api.getOrder(slug)
      .then((r) => setOrder(r.order))
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <p className="text-muted-foreground" data-page="my-order-detail" data-state="loading">Loading…</p>;
  if (error || !order) {
    return (
      <div data-page="my-order-detail" data-state="error" className="flex flex-col gap-4">
        <Breadcrumbs items={[{ label: "Events", to: "/discover" }, { label: "Shop", to: "/shop" }, { label: "My Orders", to: "/me/orders" }]} />
        <p className="text-sm text-destructive" data-error>{error ?? "Order not found"}</p>
      </div>
    );
  }

  return (
    <div data-page="my-order-detail" data-state="ready" className="flex flex-col gap-6">
      <Breadcrumbs
        items={[
          { label: "Events", to: "/discover" },
          { label: "Shop", to: "/shop" },
          { label: "My Orders", to: "/me/orders" },
          { label: `Order #${order.slug}` },
        ]}
      />
      {justSubmitted && (
        <Card className="border-primary" data-region="submitted-banner">
          <CardContent className="p-4 text-sm">
            <strong className="text-primary">Order submitted.</strong> We've emailed {order.provider.name} and sent you a receipt. They'll be in touch to arrange delivery.
          </CardContent>
        </Card>
      )}
      <div>
        <h1 className="text-2xl font-semibold">Order #{order.slug}</h1>
        <p className="text-sm text-muted-foreground">
          {order.provider.name} · Submitted {fmtDateTime(order.submitted_at)} · Status <strong className="uppercase">{order.status}</strong>
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Items</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm" data-region="order-items">
              <thead>
                <tr className="border-b border-border text-xs uppercase text-muted-foreground">
                  <th className="py-2 text-left">Item</th>
                  <th className="py-2 text-center">Qty</th>
                  <th className="py-2 text-right">Unit</th>
                  <th className="py-2 text-right">Line</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((it) => (
                  <tr key={it.id} className="border-b border-border/60" data-item="order-line">
                    <td className="py-3">
                      <p className="font-medium">{it.name}</p>
                      <p className="text-xs text-muted-foreground">{it.sku}</p>
                    </td>
                    <td className="py-3 text-center tabular-nums">{it.quantity}</td>
                    <td className="py-3 text-right tabular-nums">{fmtMoney(it.unit_price_cents, order.currency)}</td>
                    <td className="py-3 text-right font-medium tabular-nums">{fmtMoney(it.line_total_cents, order.currency)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} className="pt-3 text-right text-sm">Subtotal (list)</td>
                  <td className="pt-3 text-right text-sm tabular-nums">{fmtMoney(order.subtotal_cents, order.currency)}</td>
                </tr>
                {order.discount_cents > 0 && (
                  <tr>
                    <td colSpan={3} className="pt-1 text-right text-sm text-primary">{order.buyer_type === "school" ? "School discount" : "Parent discount"}</td>
                    <td className="pt-1 text-right text-sm tabular-nums text-primary">−{fmtMoney(order.discount_cents, order.currency)}</td>
                  </tr>
                )}
                <tr>
                  <td colSpan={3} className="pt-3 text-right text-sm font-semibold">Total</td>
                  <td className="pt-3 text-right text-base font-semibold tabular-nums">{fmtMoney(order.total_cents, order.currency)}</td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Delivery</CardTitle></CardHeader>
          <CardContent className="text-sm">
            <p className="font-medium">{order.shipping_address.line1}</p>
            {order.shipping_address.line2 && <p>{order.shipping_address.line2}</p>}
            <p>{order.shipping_address.town}, {order.shipping_address.province} {order.shipping_address.postal_code}</p>
            {order.contact_phone && <p className="mt-2 text-muted-foreground">Phone: {order.contact_phone}</p>}
            {order.notes && (
              <>
                <p className="mt-4 text-xs uppercase text-muted-foreground">Notes</p>
                <p className="mt-1 whitespace-pre-wrap">{order.notes}</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {order.status_events && order.status_events.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Progress</CardTitle></CardHeader>
          <CardContent>
            <ul className="space-y-3 text-sm" data-region="status-timeline">
              <li className="flex items-baseline gap-3">
                <span className="rounded bg-muted px-2 py-0.5 text-xs uppercase text-muted-foreground">submitted</span>
                <span className="tabular-nums">{fmtDateTime(order.submitted_at)}</span>
              </li>
              {order.status_events.map((ev) => (
                <li key={ev.id} className="flex items-baseline gap-3" data-item="status-event">
                  <span className="rounded bg-muted px-2 py-0.5 text-xs uppercase">{ev.to_status}</span>
                  <span className="tabular-nums">{fmtDateTime(ev.created_at)}</span>
                  {ev.reason && <span className="italic text-muted-foreground">-- {ev.reason}</span>}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
